import { Card } from '@design-system/Card';
import { Link } from '@design-system/Link';
import { TRACK_SAMPLE_STATUS_PATH } from '@/constants/paths';
import { Skeleton } from '@design-system/Skeleton';
import { List, ListItem } from '@design-system/List';
import { useBootup } from '@/hooks/useBootup';
import { getKitName } from '@utils/getKitName';
import { useCancerDetectUser } from '@hooks/useCancerDetectUser';

const TEST_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
} as const;

export type ITestStatus = (typeof TEST_STATUS)[keyof typeof TEST_STATUS];

export interface ITest {
  id: string;
  name: string;
  imageSrc: string;
  description: string;
  status: ITestStatus;
}

export interface AccountManagementPageTestsSectionProps
  extends React.ComponentProps<'div'> {
  tests?: ITest[];
  isLoading?: boolean;
}

const renderTests = (tests?: ITest[], isLoading?: boolean) => {
  if (isLoading) {
    return Array.from({ length: 3 }).map((_, index) => (
      <ListItem key={index} hideIndicator>
        <Skeleton className="w-full h-4 max-w-64" />
        <Skeleton className="ml-auto w-full h-4 max-w-24" />
      </ListItem>
    ));
  }

  if (!tests?.length) return null;

  const renderedTests = tests.map((test) => (
    <ListItem key={test.id} hideIndicator data-testid={`accountTests-testItem-${test.id}`}>
      <abbr className="no-underline" title={test.name}>
        <span className="truncate text-primary" data-testid={`accountTests-testName-${test.id}`}>{test.name}</span>
      </abbr>

      <span className="text-tertiary ml-auto" data-testid={`accountTests-testStatus-${test.id}`}>{test.status}</span>
    </ListItem>
  ));

  return renderedTests;
};

export const AccountManagementPageTestsSection = ({
  tests,
  isLoading: testsLoading,
}: AccountManagementPageTestsSectionProps) => {
  // Context-aware (DE-336): consumer context -> getInit v2, patient context ->
  // content API, v1 fallback. Mirrors the sidebar/home so the active-kit row
  // reflects the active context's kit. Kept the CancerDetect view toggle.
  const { bootupState, isLoading: bootupLoading } = useBootup();
  const { isCancerDetectClicked } = useCancerDetectUser();

  const isLoading = testsLoading || bootupLoading;

  const wellnessKitType = bootupState?.recommendationExists
    ? bootupState?.kitType
    : bootupState?.preRnRKitType;

  const cancerDetectKitType =
    bootupState?.CDkitDetailsData?.kitType ||
    bootupState?.CDkitDetailsData?.pre_rnr_kit_type;

  const activeKitType = isCancerDetectClicked ? cancerDetectKitType : wellnessKitType;

  const kitName = getKitName(activeKitType);

  return (
    <div className="flex flex-col" data-testid="accountTests-container">
      <span className="typography-h2 text-primary mb-6" data-testid="accountTests-title">My Tests</span>

      {isLoading ? (
        <Card className="mb-2 overflow-hidden" data-testid="accountTests-loadingCard">
          <List data-testid="accountTests-loadingList">
            <ListItem hideIndicator data-testid="accountTests-loadingItem">
              <Skeleton className="w-full h-4 max-w-64" />
              <Skeleton className="ml-auto w-full h-4 max-w-24" />
            </ListItem>
          </List>
        </Card>
      ) : kitName ? (
        <Card className="mb-2 overflow-hidden" data-testid="accountTests-activeKitCard">
          <List data-testid="accountTests-activeKitList">
            <ListItem
              hideIndicator
              className="flex justify-between items-center"
              data-testid="accountTests-activeKitItem"
            >
              <span className="text-primary" data-testid="accountTests-activeKitName">{kitName}</span>
              <span className="text-secondary" data-testid="accountTests-activeKitStatus">Active</span>
            </ListItem>
          </List>
        </Card>
      ) : null}

      <Card className="mb-2 overflow-hidden" data-testid="accountTests-testsCard">
        <List data-testid="accountTests-testsList">
          {renderTests(tests, testsLoading)}
          <ListItem asChild data-testid="accountTests-seeAllItem">
            <Link to={TRACK_SAMPLE_STATUS_PATH}>See all tests</Link>
          </ListItem>
        </List>
      </Card>
    </div>
  );
};
